import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, StatusBadge, EmptyState, Disclaimer } from "@/components/kit";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Dumbbell, Play, CheckCircle2, Headphones, Video, FileText, Clock, Repeat } from "lucide-react";

export default function MyExercises() {
  const [list, setList] = useState([]);
  const [detail, setDetail] = useState(null);

  const load = () => api.get("/exercises").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);

  const complete = async (id) => {
    const { data } = await api.post(`/exercises/${id}/complete`);
    toast.success(data.status === "completed" ? "Exercício concluído! 🎉" : "Realização registrada");
    load(); setDetail(null);
  };

  const pending = list.filter((e) => e.status !== "completed" && e.status !== "closed");
  const done = list.filter((e) => e.status === "completed");

  const ExCard = ({ e }) => (
    <Card className="p-5" data-testid={`myex-${e.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap"><span className="font-medium">{e.name}</span><StatusBadge status={e.status} /></div>
          <p className="text-sm text-muted-foreground mt-0.5">Recomendado por {e.therapist_name}</p>
          <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted-foreground">
            {e.duration && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{e.duration}</span>}
            {e.frequency && <span className="flex items-center gap-1"><Repeat className="w-3.5 h-3.5" />{e.frequency}</span>}
            {(e.start_date || e.end_date) && <span>{e.start_date} a {e.end_date}</span>}
          </div>
          <div className="mt-3 max-w-xs">
            <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>{e.completion_count} realização(ões)</span><span>{e.progress}%</span></div>
            <Progress value={e.progress} className="h-2" />
          </div>
        </div>
      </div>
      <div className="flex gap-2 mt-4">
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => setDetail(e)} data-testid={`start-${e.id}`}><Play className="w-4 h-4 mr-1" />Iniciar exercício</Button>
        {e.status !== "completed" && <Button size="sm" className="rounded-full" onClick={() => complete(e.id)} data-testid={`complete-${e.id}`}><CheckCircle2 className="w-4 h-4 mr-1" />Marcar como concluído</Button>}
      </div>
    </Card>
  );

  return (
    <div>
      <PageHeader title="Meus Exercícios" subtitle="Exercícios recomendados pelo seu hipnoterapeuta" />
      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">Pendentes ({pending.length})</TabsTrigger>
          <TabsTrigger value="done" data-testid="tab-done">Concluídos ({done.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="pending" className="space-y-3 mt-4">
          {pending.length === 0 ? <EmptyState icon={Dumbbell} title="Nenhum exercício pendente" desc="Você está em dia com suas atividades!" /> : pending.map((e) => <ExCard key={e.id} e={e} />)}
        </TabsContent>
        <TabsContent value="done" className="space-y-3 mt-4">
          {done.length === 0 ? <EmptyState icon={CheckCircle2} title="Nenhum exercício concluído ainda" /> : done.map((e) => <ExCard key={e.id} e={e} />)}
        </TabsContent>
      </Tabs>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader><DialogTitle>{detail.name}</DialogTitle></DialogHeader>
              <div className="space-y-3 text-sm">
                {detail.objective && <p><span className="font-medium">Objetivo:</span> {detail.objective}</p>}
                {detail.description && <p className="text-muted-foreground">{detail.description}</p>}
                {detail.instructions && <div className="bg-secondary/50 rounded-xl p-4"><p className="font-medium mb-1">Instruções</p><p className="text-muted-foreground">{detail.instructions}</p></div>}
                <div className="flex flex-wrap gap-2">
                  {detail.audio_url && <a href={detail.audio_url} target="_blank" rel="noreferrer"><Button variant="outline" size="sm"><Headphones className="w-4 h-4 mr-1" />Áudio</Button></a>}
                  {detail.video_url && <a href={detail.video_url} target="_blank" rel="noreferrer"><Button variant="outline" size="sm"><Video className="w-4 h-4 mr-1" />Vídeo</Button></a>}
                  {detail.pdf_url && <a href={detail.pdf_url} target="_blank" rel="noreferrer"><Button variant="outline" size="sm"><FileText className="w-4 h-4 mr-1" />PDF</Button></a>}
                </div>
                {detail.notes && <p className="text-muted-foreground"><span className="font-medium">Observações:</span> {detail.notes}</p>}
                {detail.status !== "completed" && <Button className="w-full rounded-full" onClick={() => complete(detail.id)} data-testid="detail-complete-btn"><CheckCircle2 className="w-4 h-4 mr-1" />Marcar como concluído</Button>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <Disclaimer />
    </div>
  );
}
